import { createMockDouyinPayload, douyinLiveAdapter, type DouyinMessageType, type DouyinPayload } from "./douyin";
import type { LiveEventSink, LiveEventSource, LiveUser } from "./protocol";
import type { BattleGift, BattleTeam } from "../game/battle/BattleInteractionRouter";

const ROOM_ID = "1000000000000000001";
const TEAM_USERS: Record<BattleTeam, LiveUser> = {
  ember: { id: "sim-ember-captain", displayName: "火绒队长" },
  aqua: { id: "sim-aqua-captain", displayName: "月潮队长" },
};
const GIFT_CONFIG: Record<BattleGift, { sec_gift_id: string; gift_value: number }> = {
  reinforce: { sec_gift_id: "pajama-reinforcement", gift_value: 100 },
  artillery: { sec_gift_id: "pajama-artillery", gift_value: 300 },
  flood: { sec_gift_id: "pajama-flood", gift_value: 500 },
  catrush: { sec_gift_id: "pajama-mango-cat-rush", gift_value: 650 },
  mech: { sec_gift_id: "pajama-biped-mech", gift_value: 750 },
  rift: { sec_gift_id: "pajama-earth-rift", gift_value: 800 },
  hound: { sec_gift_id: "pajama-quadruped-robo-hound", gift_value: 900 },
  tornado: { sec_gift_id: "pajama-tornado", gift_value: 1_000 },
};

export class BattleMockSource implements LiveEventSource {
  readonly sourceId = "local-douyin-battle-simulator";
  #running = false;
  #auto = true;
  #commentTimer = 0;
  #likeTimer = 0;
  #giftTimer = 0;
  #sequence = 0;

  constructor(private readonly sink: LiveEventSink) {}

  get auto(): boolean {
    return this.#auto;
  }

  setAuto(value: boolean): void {
    this.#auto = value;
  }

  start(): void {
    this.#running = true;
    this.#commentTimer = 0.2;
    this.#likeTimer = 0.65;
    this.#giftTimer = 5.5;
    this.emitJoin("ember", TEAM_USERS.ember);
    this.emitJoin("aqua", TEAM_USERS.aqua);
  }

  stop(): void {
    this.#running = false;
  }

  update(dt: number): void {
    if (!this.#running || !this.#auto) return;
    this.#commentTimer -= dt;
    this.#likeTimer -= dt;
    this.#giftTimer -= dt;
    if (this.#commentTimer <= 0) {
      const team = Math.random() < 0.5 ? "ember" : "aqua";
      this.emitCommand(team, Math.random() < 0.72 ? "冲" : "守");
      this.#commentTimer = 0.18 + Math.random() * 0.28;
    }
    if (this.#likeTimer <= 0) {
      const team = Math.random() < 0.5 ? "ember" : "aqua";
      this.emitLikes(team, 3 + Math.floor(Math.random() * 11));
      this.#likeTimer = 0.65 + Math.random() * 0.75;
    }
    if (this.#giftTimer <= 0) {
      const team = Math.random() < 0.5 ? "ember" : "aqua";
      this.emitGift(team, randomGift());
      this.#giftTimer = 5 + Math.random() * 4;
    }
  }

  emitJoin(team: BattleTeam, user = this.#user(team, "新兵")): void {
    this.#publish("live_comment", user, { content: team === "ember" ? "红队" : "蓝队" });
  }

  emitCommand(team: BattleTeam, command: "冲" | "守"): void {
    this.#publish("live_comment", TEAM_USERS[team], { content: command });
  }

  emitLikes(team: BattleTeam, count = 20): void {
    this.#publish("live_like", TEAM_USERS[team], { like_num: count });
  }

  emitFollow(team: BattleTeam): void {
    this.#publish("live_fansclub", this.#user(team, "新关注"), { fansclub_reason_type: 2, fansclub_level: 1 });
  }

  emitGift(team: BattleTeam, gift: BattleGift): void {
    this.#publish("live_gift", TEAM_USERS[team], { ...GIFT_CONFIG[gift], gift_num: 1 });
  }

  seedBattle(players = 1_000): void {
    const safeCount = Math.min(50_000, Math.max(0, Math.floor(players)));
    for (let index = 0; index < safeCount; index += 1) {
      const team: BattleTeam = index % 2 === 0 ? "ember" : "aqua";
      this.emitJoin(team, { id: `battle-load-${index}`, displayName: `${team === "ember" ? "火绒" : "月潮"}观众${index + 1}` });
    }
  }

  replayChaos(players = 240): void {
    const events = Array.from({ length: players }, (_, index) => {
      const team: BattleTeam = index % 2 === 0 ? "ember" : "aqua";
      const user = { id: `battle-replay-${index}`, displayName: `对抗观众${index + 1}` };
      return {
        type: "live_comment" as const,
        payload: createMockDouyinPayload("live_comment", user, {
          msg_id: `battle-replay-message-${index}`,
          content: team === "ember" ? "红队" : "蓝队",
          timestamp: Date.now() - (index % 7) * 1_000,
        }),
      };
    });
    for (let offset = 0; offset < events.length; offset += 24) {
      const chunk = events.slice(offset, offset + 24).reverse();
      window.setTimeout(() => {
        for (const [index, event] of chunk.entries()) {
          this.#publishPayload(event.type, event.payload);
          if (index % 9 === 0) this.#publishPayload(event.type, event.payload);
        }
      }, (offset / 24) * 28);
    }
    window.setTimeout(() => this.emitGift("ember", "artillery"), 80);
    window.setTimeout(() => this.emitGift("aqua", "flood"), 260);
    window.setTimeout(() => this.emitGift("ember", "catrush"), 460);
    window.setTimeout(() => this.emitGift("aqua", "mech"), 680);
    window.setTimeout(() => this.emitGift("ember", "rift"), 920);
    window.setTimeout(() => this.emitGift("aqua", "hound"), 1_160);
    window.setTimeout(() => this.emitGift("aqua", "tornado"), 1_450);
  }

  #user(team: BattleTeam, label: string): LiveUser {
    this.#sequence += 1;
    return { id: `sim-${team}-${this.#sequence}`, displayName: `${team === "ember" ? "火绒" : "月潮"}${label}` };
  }

  #publish(messageType: DouyinMessageType, user: LiveUser, fields: Partial<DouyinPayload>): void {
    this.#publishPayload(messageType, createMockDouyinPayload(messageType, user, fields));
  }

  #publishPayload(messageType: DouyinMessageType, payload: DouyinPayload): void {
    const events = douyinLiveAdapter.normalize({
      headers: { "x-msg-type": messageType, "x-roomid": ROOM_ID },
      body: [payload],
    });
    for (const event of events) this.sink.publish(event);
  }
}

function randomGift(): BattleGift {
  const roll = Math.random();
  if (roll < 0.6) return "reinforce";
  if (roll < 0.72) return "artillery";
  if (roll < 0.79) return "flood";
  if (roll < 0.85) return "catrush";
  if (roll < 0.9) return "mech";
  if (roll < 0.94) return "rift";
  if (roll < 0.98) return "hound";
  return "tornado";
}
